import { apiUrl, getResolvedCatalogApiRoot } from '../config';

const jsonHeaders = { Accept: 'application/json' };

function fetchOpts(signal) {
  const opts = { cache: 'no-store', headers: jsonHeaders };
  if (signal) opts.signal = signal;
  return opts;
}

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

function httpErrorMessage(label, res, body) {
  const hint = body?.message ? String(body.message) : '';
  const tail = hint ? `: ${hint}` : '';
  return `${label} failed (${res.status} ${res.statusText || ''})${tail}`;
}

/** @param {AbortSignal} [signal] */
export async function fetchCategories(signal) {
  const url = apiUrl('/categories');
  const res = await fetch(url, fetchOpts(signal));
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      `${httpErrorMessage('Categories', res, body)} — URL: ${url} — API root: ${getResolvedCatalogApiRoot()}`,
    );
  }
  // Handle { data: [...] }, { categories: [...] }, or bare array
  const list = Array.isArray(body.data) ? body.data : (Array.isArray(body.categories) ? body.categories : []);
  return list;
}

/** @param {AbortSignal} [signal] */
/** @returns {{ products: object[] }} */
export async function fetchShopProducts(signal) {
  const url = apiUrl('/products');
  const res = await fetch(url, fetchOpts(signal));
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      `${httpErrorMessage('Products', res, body)} — URL: ${url} — API root: ${getResolvedCatalogApiRoot()}`,
    );
  }
  // Handle multiple Laravel response shapes:
  //   { data: { products: [...] } }  (custom resource)
  //   { data: [...] }                (standard ResourceCollection)
  //   { products: [...] }            (direct)
  let products = body.data?.products ?? body.data ?? body.products ?? [];
  if (!Array.isArray(products)) products = [];
  return { products };
}

/** @param {AbortSignal} [signal] */
export async function fetchProductBySlug(slug, signal) {
  const url = apiUrl(`/products/${encodeURIComponent(slug)}`);
  const res = await fetch(url, fetchOpts(signal));
  const body = await parseJson(res);
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(httpErrorMessage('Product', res, body));
  }
  return body.data || null;
}

export async function submitOrder(payload, bearerToken) {
  const url = apiUrl('/orders');
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const msg = body.message || (body.errors && JSON.stringify(body.errors)) || `Order failed (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : 'Order failed');
  }
  return body.data;
}
